import { Link, useLocation } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { isTabActive, type ContributorTab } from "./contributor-tabs";

const pill = "inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-sm font-medium transition-colors";
const idle = "border-border/60 bg-surface text-foreground hover:bg-surface-2";
const current = "border-foreground bg-foreground text-background";

/** The contributor's work as short verbs; related tasks open from a small menu (desktop). */
export function ContributorTabs({ tabs, label }: { tabs: ContributorTab[]; label: string }) {
  const { pathname } = useLocation();
  const here = pathname.replace(/\/+$/, "");

  return (
    <nav aria-label={label} className="hidden items-center gap-1.5 lg:flex">
      {tabs.map((tab) => {
        const active = isTabActive(tab, pathname);
        if (!tab.items) {
          return (
            <Link key={tab.key} to={tab.href} aria-current={active ? "page" : undefined} className={cn(pill, active ? current : idle)}>
              <Icon name={tab.icon} className={cn("size-4", active && "text-primary")} />
              {tab.label}
              {tab.badge ? <span className="tabular-nums text-xs font-semibold opacity-70">{tab.badge}</span> : null}
            </Link>
          );
        }
        return (
          <DropdownMenu key={tab.key}>
            <DropdownMenuTrigger asChild>
              <button type="button" className={cn(pill, active ? current : idle)}>
                <Icon name={tab.icon} className={cn("size-4", active && "text-primary")} />
                {tab.label}
                <Icon name="chevronDown" className="size-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" sideOffset={8} className="w-72 rounded-xl border-border/60 bg-popover p-1">
              {tab.items.map((task) => (
                <DropdownMenuItem key={task.key} asChild className="cursor-pointer gap-3 rounded-md px-3 py-2">
                  <Link to={task.href} aria-current={here === task.href ? "page" : undefined}>
                    <span className="grid size-8 shrink-0 place-items-center rounded-md bg-brand/10 text-brand">
                      <Icon name={task.icon} className="size-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground">{task.label}</span>
                      <span className="block truncate text-xs text-muted-foreground">{task.hint}</span>
                    </span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
    </nav>
  );
}
